import { useMemo } from 'preact/hooks';
import { HOSTED_URL, PRICING } from '../data/content';
import './Signup.scss';

/**
 * Signup landing page. Reads `?plan=<tier>` so the post-OAuth wizard
 * (server-side `signup.complete` then admin SPA) can pre-select the
 * tier the user came in expecting. OAuth start links go to the
 * server's `/api/v1/auth/<provider>/start` endpoint, which 302s to the
 * provider and back to the admin SPA.
 *
 * Deliberately minimal — one card, two OAuth options, the chosen plan
 * displayed at the top so the user has confidence they're paying for
 * (or trying) what they clicked. No form fields: OAuth provides email
 * and name, the wizard does the org-name step.
 */
export function Signup() {
	const plan = useMemo(() => {
		if (typeof window === 'undefined') return null;
		const params = new URLSearchParams(window.location.search);
		const planId = params.get('plan');
		return PRICING.find((p) => p.tier === planId) ?? null;
	}, []);

	// The marketing site is statically hosted, so the OAuth endpoints
	// live on the backend at HOSTED_URL. `final` is where the OAuth
	// callback should land — the admin SPA's onboarding flow at /dashboard.
	const finalRedirect = `${HOSTED_URL}/dashboard/onboarding`;
	const githubStart = `${HOSTED_URL}/api/v1/auth/github/start?finalRedirect=${encodeURIComponent(finalRedirect)}&plan=${encodeURIComponent(plan?.tier ?? 'free')}`;
	const googleStart = `${HOSTED_URL}/api/v1/auth/google/start?finalRedirect=${encodeURIComponent(finalRedirect)}&plan=${encodeURIComponent(plan?.tier ?? 'free')}`;

	return (
		<section class="m-signup" id="signup">
			<div class="m-signup__inner">
				<header class="m-signup__head">
					<span class="m-signup__eyebrow">create your account</span>
					<h1 class="m-signup__title">
						two clicks to your first <em class="m-signup__title-em">commented page.</em>
					</h1>
					<p class="m-signup__sub">
						Sign up with GitHub or Google. We'll spin up a personal organization, seed a
						sandbox project so the toolbar appears the moment you land, and drop you
						straight into the admin dashboard.
					</p>
				</header>

				{plan && (
					<div class="m-signup__plan">
						<span class="m-signup__plan-label">You picked</span>
						<span class="m-signup__plan-name">{plan.name}</span>
						<span class="m-signup__plan-price">
							{plan.price}
							{plan.period ? ` ${plan.period}` : ''}
						</span>
						{plan.popular && <span class="m-signup__plan-badge">popular</span>}
					</div>
				)}

				<div class="m-signup__providers">
					<a class="m-signup__provider" href={githubStart} data-provider="github">
						<span class="m-signup__provider-icon" aria-hidden="true">
							<svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
								<path d="M12 .5C5.65.5.5 5.65.5 12c0 5.08 3.29 9.39 7.86 10.91.58.1.79-.25.79-.56v-2.2c-3.2.7-3.88-1.36-3.88-1.36-.52-1.32-1.27-1.67-1.27-1.67-1.04-.71.08-.7.08-.7 1.15.08 1.76 1.18 1.76 1.18 1.02 1.75 2.68 1.24 3.34.95.1-.74.4-1.24.73-1.52-2.55-.29-5.24-1.27-5.24-5.66 0-1.25.45-2.27 1.18-3.07-.12-.29-.51-1.45.11-3.03 0 0 .96-.31 3.16 1.17.91-.25 1.89-.38 2.86-.38.97 0 1.95.13 2.86.38 2.2-1.48 3.16-1.17 3.16-1.17.62 1.58.23 2.74.11 3.03.74.8 1.18 1.82 1.18 3.07 0 4.4-2.69 5.37-5.26 5.65.41.36.78 1.06.78 2.14v3.17c0 .31.21.67.79.56C20.21 21.38 23.5 17.08 23.5 12 23.5 5.65 18.35.5 12 .5z" />
							</svg>
						</span>
						Continue with GitHub
						<span class="m-signup__provider-arrow" aria-hidden="true" />
					</a>
					<a
						class="m-signup__provider m-signup__provider--google"
						href={googleStart}
						data-provider="google"
					>
						<span class="m-signup__provider-icon" aria-hidden="true">
							<svg viewBox="0 0 24 24" width="22" height="22">
								<path
									fill="#4285F4"
									d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
								/>
								<path
									fill="#34A853"
									d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
								/>
								<path
									fill="#FBBC05"
									d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
								/>
								<path
									fill="#EA4335"
									d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
								/>
							</svg>
						</span>
						Continue with Google
						<span class="m-signup__provider-arrow" aria-hidden="true" />
					</a>
				</div>

				<p class="m-signup__legal">
					By signing up you agree to our <a href="/legal/terms">Terms of Service</a> and{' '}
					<a href="/legal/privacy">Privacy Policy</a>. No card required — the Free tier is
					your trial.
				</p>

				<div class="m-signup__alt">
					Already have an account? <a href={`${HOSTED_URL}/dashboard`}>Sign in</a>
				</div>
			</div>
		</section>
	);
}
